import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  BeneficiaryDto,
  BeneficiaryPayload,
} from "@/features/wallet/api/dto";

/**
 * Enregistre un bénéficiaire (`POST /Beneficiaries/add`) — portefeuille
 * FlashPay, compte bancaire ou mobile money. Le numéro de compte est hashé
 * pour l'unicité et chiffré pour le stockage côté serveur (guide §6.3).
 */
export const useCreateBeneficiary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "beneficiaries", "create"],
    mutationFn: (body: BeneficiaryPayload) =>
      mutateData({
        url: walletEndpoints.beneficiaries.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<BeneficiaryDto>,
    onSuccess: (_data, variables) => {
      return invalidateAfterMutation(
        queryClient,
        [QUERY_KEYS.WALLET_BENEFICIARIES(variables.walletId)],
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateBeneficiary failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
